import * as RadixCheckbox from '@radix-ui/react-checkbox';
import * as React from 'react';
import { withOnClickTracking } from 'auto-tracking';
import { TrackingProperties } from 'common-tracking';
import { forwardRef } from 'react';

import { styled, theme } from '../stitches.config';
import { CheckMarkIcon } from './icons/check-mark';

const StyledRoot = styled(RadixCheckbox.Root, {
  all: 'initial',
  color: theme.colors.gray1,
  borderRadius: '3px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,

  '&[data-state=unchecked]': {
    backgroundColor: theme.colors.gray1,
    border: `1px solid ${theme.colors.gray8}`,

    '&:active': {
      backgroundColor: theme.colors.gray3,
    },
  },

  '&[data-state=checked]': {
    backgroundColor: theme.colors.blue9,

    '&:active': {
      backgroundColor: theme.colors.blue10,
    },

    '&[data-disabled]': {
      opacity: 0.5,
      pointerEvents: 'none',
    },
  },

  '&:focus-visible': {
    outline: `${theme.colors.yellow8} solid 2px`,
    outlineOffset: '2px',
  },

  variants: {
    error: {
      false: {},
      true: {
        '&[data-state=unchecked]': {
          backgroundColor: theme.colors.red1,
          borderColor: theme.colors.red8,
        },
      },
    },
    variant: {
      default: {},
      yellow: {
        '&[data-state=checked]': {
          backgroundColor: theme.colors.yellow8,

          '&:active': {
            backgroundColor: theme.colors.yellow9,
          },
        },
      },
    },
    size: {
      default: {
        '&[data-state=unchecked]': {
          width: '22px',
          height: '22px',
        },
        '&[data-state=checked]': {
          width: '24px',
          height: '24px',
        },
      },
      small: {
        '&[data-state=unchecked]': {
          width: '16px',
          height: '16px',
        },
        '&[data-state=checked]': {
          width: '18px',
          height: '18px',
        },
      },
    },
  },
  defaultVariants: {
    error: 'false',
    variant: 'default',
    size: 'default',
  },
});

const StyledIndicator = styled(RadixCheckbox.Indicator, {
  width: '14px',
});

type CheckboxProps = React.ComponentProps<typeof StyledRoot> & {
  trackingName?: string;
  trackingProperties?: TrackingProperties;
};

const StyledRootWithTrack = withOnClickTracking(StyledRoot);

export const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(
  (props, ref) => (
    <StyledRootWithTrack
      ref={ref}
      trackingProperties={{
        typeofCTA: 'checkbox',
      }}
      {...props}
    >
      <StyledIndicator>
        <CheckMarkIcon />
      </StyledIndicator>
    </StyledRootWithTrack>
  ),
);
